import i18n from '../i18n';
import { MyDesignQR } from '../models/MyDesignQR';
import {
  CameraMessageResources,
  getCameraMessageResources,
  getPhotoLibraryMessageResources,
  PhotoLibraryMessageResources
} from './MessageResources';
import { analyzeQrBinary } from './MyDesignParser';

declare let window: Window & {
  openQRCodeReader?: (optionsString: string) => void;
  openQRCodeReaderFromPhotoLibrary?: (optionsString: string) => void;
  onQRCodeRead?: (base64EncodeText?: string) => void;
  closeQRCodeReader?: () => void;
  closeQRCodeReaderFromPhotoLibrary?: () => void;
  sendMessage?: (jsonString: string) => void;
};

export type QRSource = 'camera' | 'photoLibrary';

export type MessageType = 'B_SHOW_SUCCESS' | 'B_SHOW_ERROR' | 'B_SET_INDEX';

// 読み込み中のQRコードマイデザインを保持する
let progress: MyDesignQR[] = [];

interface QRCodeReaderOptions {
  messageResources: CameraMessageResources | PhotoLibraryMessageResources;
}

function openQRCodeReader(source: QRSource, successCallback: (result: MyDesignQR[]) => void) {
  switch (source) {
    case 'camera':
      if (window.openQRCodeReader) {
        progress = [];
        const messageResources = getCameraMessageResources();
        const options: QRCodeReaderOptions = { messageResources };
        window.openQRCodeReader(JSON.stringify(options));
        window.onQRCodeRead = onQRCodeRead(source, successCallback);
      }
      break;
    case 'photoLibrary':
      if (window.openQRCodeReaderFromPhotoLibrary) {
        progress = [];
        const messageResources = getPhotoLibraryMessageResources();
        const options: QRCodeReaderOptions = { messageResources };
        window.openQRCodeReaderFromPhotoLibrary(JSON.stringify(options));
        window.onQRCodeRead = onQRCodeRead(source, successCallback);
      }
      break;
  }
}

function closeQRCodeReader(source: QRSource) {
  switch (source) {
    case 'camera':
      if (window.closeQRCodeReader) {
        window.closeQRCodeReader();
        progress = [];
      }
      break;
    case 'photoLibrary':
      if (window.closeQRCodeReaderFromPhotoLibrary) {
        window.closeQRCodeReaderFromPhotoLibrary();
        progress = [];
      }
      break;
  }
}

const errorPleaseReadNth = (i: number) => i18n.t(`coral:error_please_read_nth_${i}`);

function onQRCodeRead(source: QRSource, callback: (result: MyDesignQR[]) => void) {
  return (base64EncodeText?: string) => {
    if (!base64EncodeText) {
      sendMessage({
        type: 'B_SHOW_ERROR',
        message: i18n.t(`coral:error_unknown`)
      });
      return;
    }

    const myDesign: MyDesignQR | undefined = analyzeQrBinary(base64EncodeText);
    if (!myDesign) {
      sendMessage({
        type: 'B_SHOW_ERROR',
        message: i18n.t(`coral:error_invalid_qr`)
      });
      return;
    }

    // 角つきぼうし（usage = 6）と角なしぼうし（usage = 7）はPROであるがQRは1枚のみなので，
    // それ以外のPROの場合は2枚目以降を読み込む
    if (myDesign.designType === 'pro' && myDesign.usage !== 6 && myDesign.usage !== 7) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const idx = myDesign.sheetIndex!;
      if (progress.length !== idx) {
        sendMessage({
          type: 'B_SHOW_ERROR',
          message: errorPleaseReadNth(progress.length)
        });
        return;
      }
      if (idx === 0) {
        successMyDesignPro(myDesign, 0);
      } else {
        const [first] = progress;
        if (
          myDesign.consecutiveId !== undefined &&
          first.consecutiveId === myDesign.consecutiveId
        ) {
          successMyDesignPro(myDesign, idx);
          if (idx === 3) {
            callback([...progress]);
            closeQRCodeReader(source);
          }
        } else {
          sendMessage({
            type: 'B_SHOW_ERROR',
            message: i18n.t(`coral:error_different_design`)
          });
        }
      }
    } else {
      if (progress.length !== 0) {
        sendMessage({
          type: 'B_SHOW_ERROR',
          message: errorPleaseReadNth(progress.length)
        });
        return;
      }
      // send success with empty message for the sake of haptic feedback
      sendMessage({ type: 'B_SHOW_SUCCESS', message: '' });
      callback([myDesign]);
      closeQRCodeReader(source);
    }
  };
}

function sendMessage(payload: { type: MessageType; message: string }) {
  if (!window.sendMessage) {
    return;
  }
  window.sendMessage(JSON.stringify(payload));
}

export { openQRCodeReader, closeQRCodeReader, sendMessage };

function successMyDesignPro(myDesign: MyDesignQR, index: number, message = '') {
  sendMessage({
    type: 'B_SHOW_SUCCESS',
    message
  });
  sendMessage({ type: 'B_SET_INDEX', message: String(index) });
  progress.push(myDesign);
}
